import React, { useState, useEffect } from 'react';
import { FileText, Plus, Trash2, Upload, Shield, CheckCircle2, AlertTriangle, XCircle, CalendarClock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface ComplianceDocument {
  id?: string;
  document_type: string;
  document_url: string;
  expiration_date?: string | null;
  issuing_authority?: string | null;
  certification_number?: string | null;
  status?: string | null;
}

const DOCUMENT_TYPES = [
  'Capability Statement',
  'Business License',
  'Professional Certification',
  'Insurance Certificate',
  'Bonding Information',
  'Quality Certification (ISO/CMMI)',
  'Safety Certification',
  'Set-Aside / Socioeconomic Certification',
  'Facility Clearance',
  'SAM.gov Registration',
  'Other',
];

// Derive a freshness status from an expiration date.
function expiryInfo(expiration?: string | null) {
  if (!expiration) return { key: 'none' as const, days: null as number | null };
  const days = Math.ceil((new Date(expiration).getTime() - Date.now()) / 86400000);
  if (days < 0) return { key: 'expired' as const, days };
  if (days <= 30) return { key: 'expiring' as const, days };
  if (days <= 60) return { key: 'soon' as const, days };
  return { key: 'valid' as const, days };
}

function ExpiryBadge({ expiration }: { expiration?: string | null }) {
  const { key, days } = expiryInfo(expiration);
  const map = {
    none: { cls: 'text-bw-muted bg-bw-surface-subtle border-bw-border', icon: CalendarClock, label: 'No expiry' },
    valid: { cls: 'text-success bg-success-bg border-success-border', icon: CheckCircle2, label: `Valid · ${days}d left` },
    soon: { cls: 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border', icon: CalendarClock, label: `${days}d left` },
    expiring: { cls: 'text-fit-moderate bg-orange-50 border-orange-200', icon: AlertTriangle, label: `Expiring · ${days}d` },
    expired: { cls: 'text-urgent bg-urgent-bg border-urgent-border', icon: XCircle, label: `Expired ${Math.abs(days || 0)}d ago` },
  }[key];
  const Icon = map.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${map.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {map.label}
    </span>
  );
}

export function ComplianceDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  useEffect(() => { loadDocuments(); }, [user]);

  const loadDocuments = async () => {
    try {
      if (!user) return;
      const { data, error } = await supabase
        .from('compliance_documents')
        .select('*')
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading compliance documents:', error);
      toast.error('Failed to load compliance documents');
    } finally {
      setLoading(false);
    }
  };

  const addDocument = () =>
    setDocuments((prev) => [{ document_type: '', document_url: '', expiration_date: null, issuing_authority: '', certification_number: '' }, ...prev]);

  const removeDocument = async (index: number) => {
    try {
      const doc = documents[index];
      if (doc.id) {
        const { error } = await supabase.from('compliance_documents').delete().eq('id', doc.id);
        if (error) throw error;
        if (doc.document_url) {
          const fileName = doc.document_url.split('/').pop();
          if (fileName) await supabase.storage.from('compliance-documents').remove([`${user!.id}/${fileName}`]);
        }
      }
      setDocuments((prev) => prev.filter((_, i) => i !== index));
      toast.success('Document removed');
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove document');
    }
  };

  const change = (index: number, field: keyof ComplianceDocument, value: string | null) =>
    setDocuments((prev) => prev.map((doc, i) => (i === index ? { ...doc, [field]: value } : doc)));

  const handleFileUpload = async (index: number, file: File) => {
    try {
      setUploadingFor(documents[index].id || `new-${index}`);
      const fileExt = file.name.split('.').pop();
      const fileName = `${user!.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('compliance-documents').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('compliance-documents').getPublicUrl(fileName);
      change(index, 'document_url', publicUrl);
      toast.success('Document uploaded');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (documents.some((d) => !d.document_type || !d.document_url)) {
        toast.error('Each document needs a type and an uploaded file.');
        return;
      }
      const results = await Promise.all(
        documents.map((doc) => {
          const status = expiryInfo(doc.expiration_date).key === 'expired'
            ? 'expired'
            : expiryInfo(doc.expiration_date).key === 'expiring'
            ? 'expiring_soon'
            : 'active';
          const row = {
            profile_id: user!.id,
            document_type: doc.document_type,
            document_url: doc.document_url,
            expiration_date: doc.expiration_date || null,
            issuing_authority: doc.issuing_authority || null,
            certification_number: doc.certification_number || null,
            status,
          };
          return doc.id
            ? supabase.from('compliance_documents').update(row).eq('id', doc.id)
            : supabase.from('compliance_documents').insert([row]);
        }),
      );
      if (results.some((r) => r.error)) throw new Error('Failed to save some documents');
      await loadDocuments();
      toast.success('Compliance documents saved');
    } catch (error) {
      console.error('Error saving compliance documents:', error);
      toast.error('Failed to save compliance documents');
    } finally {
      setSaving(false);
    }
  };

  const expiredCount = documents.filter((d) => expiryInfo(d.expiration_date).key === 'expired').length;
  const expiringCount = documents.filter((d) => ['expiring', 'soon'].includes(expiryInfo(d.expiration_date).key)).length;

  if (loading) {
    return (
      <div className="bg-bw-surface rounded-lg border border-bw-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-24 bg-gray-200 rounded" />
          <div className="h-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-bw-surface rounded-lg border border-bw-border p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
          <h3 className="text-lg font-semibold flex items-center">
            <Shield className="h-5 w-5 mr-2 text-bw-muted" />
            Compliance &amp; Certifications
          </h3>
          <button type="button" onClick={addDocument} className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:brightness-110">
            <Plus className="h-4 w-4 mr-2" /> Add Document
          </button>
        </div>
        <p className="text-sm text-bw-muted">
          Track certifications, licenses, and registrations with their <strong>expiration dates</strong> so BidWin can flag
          lapses before a deadline and cite them accurately in the compliance section.
        </p>
        {(expiredCount > 0 || expiringCount > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {expiredCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-urgent-border bg-urgent-bg text-urgent px-2.5 py-1 text-xs font-medium">
                <XCircle className="h-3.5 w-3.5" /> {expiredCount} expired
              </span>
            )}
            {expiringCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 text-fit-moderate px-2.5 py-1 text-xs font-medium">
                <AlertTriangle className="h-3.5 w-3.5" /> {expiringCount} expiring soon
              </span>
            )}
          </div>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-10 bg-bw-surface-subtle rounded-lg border-2 border-dashed border-bw-border">
          <Shield className="h-12 w-12 text-bw-muted mx-auto mb-3" />
          <p className="text-bw-ink font-medium">No compliance documents yet</p>
          <button type="button" onClick={addDocument} className="mt-2 text-bw-primary hover:underline">
            Add your first certification or license
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc, index) => (
            <div key={doc.id || index} className="bg-bw-surface border border-bw-border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-bw-ink">{doc.document_type || `Document ${index + 1}`}</span>
                  <ExpiryBadge expiration={doc.expiration_date} />
                </div>
                <button type="button" onClick={() => removeDocument(index)} className="text-urgent hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-bw-muted mb-1">Document Type *</label>
                  <select
                    value={doc.document_type}
                    onChange={(e) => change(index, 'document_type', e.target.value)}
                    className="block w-full rounded-md border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    required
                  >
                    <option value="">Select type…</option>
                    {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-bw-muted mb-1">Expiration Date</label>
                  <input
                    type="date"
                    value={doc.expiration_date ? doc.expiration_date.slice(0, 10) : ''}
                    onChange={(e) => change(index, 'expiration_date', e.target.value || null)}
                    className="block w-full rounded-md border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-bw-muted mb-1">Issuing Authority</label>
                  <input
                    type="text"
                    value={doc.issuing_authority || ''}
                    onChange={(e) => change(index, 'issuing_authority', e.target.value)}
                    placeholder="e.g. SBA, ISO, State of Virginia"
                    className="block w-full rounded-md border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-bw-muted mb-1">Certification / License #</label>
                  <input
                    type="text"
                    value={doc.certification_number || ''}
                    onChange={(e) => change(index, 'certification_number', e.target.value)}
                    placeholder="e.g. 9001-2015-12345"
                    className="block w-full rounded-md border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-bw-muted mb-1">Document File *</label>
                  {doc.document_url ? (
                    <div className="flex items-center gap-3">
                      <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-bw-primary hover:underline text-sm">
                        <FileText className="h-4 w-4 mr-1.5" /> View document
                      </a>
                      <button type="button" onClick={() => change(index, 'document_url', '')} className="text-urgent hover:text-red-700 text-sm inline-flex items-center">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Replace
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(index, f); }}
                        className="hidden"
                        id={`compliance-doc-${index}`}
                        disabled={uploadingFor === (doc.id || `new-${index}`)}
                      />
                      <label
                        htmlFor={`compliance-doc-${index}`}
                        className={`flex items-center justify-center w-full px-4 py-2 border border-dashed border-bw-border rounded-md text-sm font-medium text-bw-ink bg-bw-surface-subtle hover:bg-bw-surface cursor-pointer ${
                          uploadingFor === (doc.id || `new-${index}`) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingFor === (doc.id || `new-${index}`) ? 'Uploading…' : 'Upload file (PDF, DOC, image)'}
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-bw-primary text-white rounded-md hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save all changes'}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
